import React, { useMemo, useState } from 'react';
import { IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import GroupSwapIcon from '@mui/icons-material/SwapHoriz';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Card, CardBody, StatusPill, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import CapacityEditor from '../../editors/CapacityEditor';
import SpecialtyEditor from '../../editors/SpecialtyEditor';
import EditableCell from '../../editors/EditableCell';
import CommercialEditDrawer, { CommercialEditMode } from '../../drawers/CommercialEditDrawer';
import { Commercial, FlowTypeDef, effectiveLeads } from '../../PanelComerciales.types';

interface CommercialsTableProps {
    commercials: Commercial[];
    teamOptions: string[];
    flowTypes: FlowTypeDef[];
    onCommercialUpdate: (next: Commercial, prev: Commercial) => void;
}

const STATUS_VARIANT = {
    'Activo': 'success',
    'Inactivo': 'muted',
    'Bloqueado': 'danger',
} as const;

const CommercialsTable: React.FC<CommercialsTableProps> = ({
    commercials,
    teamOptions,
    flowTypes,
    onCommercialUpdate,
}) => {
    const [teamFilter, setTeamFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [search, setSearch] = useState<string>('');

    const [drawerMode, setDrawerMode] = useState<CommercialEditMode | null>(null);
    const [drawerCommercial, setDrawerCommercial] = useState<Commercial | null>(null);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return commercials.filter((c) => {
            if (teamFilter && c.team !== teamFilter) return false;
            if (statusFilter && c.status !== statusFilter) return false;
            if (term && !c.name.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [commercials, teamFilter, statusFilter, search]);

    const counts = useMemo(
        () => ({
            Activo: commercials.filter((c) => c.status === 'Activo').length,
            Inactivo: commercials.filter((c) => c.status === 'Inactivo').length,
            Bloqueado: commercials.filter((c) => c.status === 'Bloqueado').length,
        }),
        [commercials],
    );

    const updateField = <K extends keyof Commercial>(c: Commercial, key: K, value: Commercial[K]) => {
        onCommercialUpdate({ ...c, [key]: value }, c);
    };

    const setSpecialty = (c: Commercial, specialty: Record<string, number>) => {
        onCommercialUpdate({ ...c, specialty }, c);
    };

    const openDrawer = (mode: CommercialEditMode, c: Commercial) => {
        setDrawerMode(mode);
        setDrawerCommercial(c);
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        setDrawerCommercial(null);
    };

    return (
        <Card>
            <SectionHeader
                title='Comerciales'
                subtitle={`${counts.Activo} activos · ${counts.Inactivo} inactivos · ${counts.Bloqueado} bloqueados`}
                icon={<PersonOutlineIcon style={{ color: '#1E7BFF' }} />}
                actions={
                    <div style={{ display: 'flex', gap: 8 }}>
                        <TextField
                            size='small'
                            placeholder='Buscar'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ width: 200 }}
                        />
                        <TextField
                            size='small'
                            select
                            value={teamFilter}
                            onChange={(e) => setTeamFilter(e.target.value)}
                            sx={{ width: 140 }}
                            label='Equipo'
                        >
                            <MenuItem value=''>Todos</MenuItem>
                            {teamOptions.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size='small'
                            select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ width: 140 }}
                            label='Estado'
                        >
                            <MenuItem value=''>Todos</MenuItem>
                            <MenuItem value='Activo'>Activo</MenuItem>
                            <MenuItem value='Inactivo'>Inactivo</MenuItem>
                            <MenuItem value='Bloqueado'>Bloqueado</MenuItem>
                        </TextField>
                    </div>
                }
            />
            <CardBody>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Equipo</th>
                                <th>Estado</th>
                                <th>Capacidad %</th>
                                <th>Capacidad # Deals</th>
                                <th>Deals (T / Open / Closed)</th>
                                <th>Especialidad</th>
                                <th>Meta</th>
                                <th>Ausencia</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id}>
                                    <td><strong>{c.name}</strong></td>
                                    <td>{c.team}</td>
                                    <td>
                                        <StatusPill $variant={STATUS_VARIANT[c.status]}>
                                            {c.status}
                                        </StatusPill>
                                    </td>
                                    <td>
                                        <CapacityEditor
                                            value={c.capacity}
                                            onChange={(next) => updateField(c, 'capacity', next)}
                                        />
                                    </td>
                                    <td>
                                        <Tooltip title='Asignado por el cronjob de scoring · no editable' arrow>
                                            <strong>{c.leadsEfectivos ?? effectiveLeads(c)}</strong>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Tooltip title='Total / Abiertos / Cerrados (WON+LOST) · alimentado por Pipedrive · no editable' arrow>
                                            <span style={{ display: 'inline-flex', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
                                                <strong>{c.totalDeals ?? 0}</strong>
                                                <span style={{ color: '#9CA3AF' }}>/</span>
                                                <span title='Open' style={{ color: '#10B981' }}>{c.openDeals ?? 0}</span>
                                                <span style={{ color: '#9CA3AF' }}>/</span>
                                                <span title='Closed' style={{ color: '#6B7280' }}>{c.closedDeals ?? 0}</span>
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <SpecialtyEditor
                                            value={c.specialty}
                                            flowTypes={flowTypes}
                                            onChange={(next) => setSpecialty(c, next)}
                                        />
                                    </td>
                                    <td>
                                        <EditableCell
                                            value={c.goal}
                                            onCommit={(next) => updateField(c, 'goal', next)}
                                        />
                                    </td>
                                    <td>{c.absence || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Editar'>
                                            <IconButton size='small' onClick={() => openDrawer('full', c)}>
                                                <EditOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Cambiar equipo'>
                                            <IconButton size='small' onClick={() => openDrawer('team', c)}>
                                                <GroupSwapIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Ajustar meta'>
                                            <IconButton size='small' onClick={() => openDrawer('goal', c)}>
                                                <PaidOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Registrar ausencia'>
                                            <IconButton size='small' onClick={() => openDrawer('absence', c)}>
                                                <PauseCircleOutlineIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Bloquear'>
                                            <IconButton
                                                size='small'
                                                onClick={() => updateField(c, 'status', 'Bloqueado')}
                                            >
                                                <LockOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                                        No hay comerciales que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            {drawerMode && drawerCommercial && (
                <CommercialEditDrawer
                    open={Boolean(drawerMode)}
                    mode={drawerMode}
                    commercial={drawerCommercial}
                    teamOptions={teamOptions}
                    onClose={closeDrawer}
                    onSave={(next) => {
                        onCommercialUpdate(next, drawerCommercial);
                        closeDrawer();
                    }}
                />
            )}
        </Card>
    );
};

export default CommercialsTable;
